import { useMemo, useState } from "react"
import { Clock } from "lucide-react"
import { Dialog, DialogBody, DialogFooter, DialogHeader } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ActivityFeedPane } from "./activity-feed-pane"
import { cn } from "@/lib/cn"
import type { Activity } from "@/types/activity"

type Props = {
  entries: Activity[]
  // Optional override label for the dialog title (e.g. "Skill activity").
  title?: string
}

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

// Compact icon-only Clock button that opens a wide modal containing the
// full <ActivityFeedPane>. Drops into page headers / toolbars where an
// inline timeline would crowd out the primary content. The badge shows
// activity in the last 7 days; suppressed when zero.
export function ActivityFeedButton({ entries, title = "Activity" }: Props) {
  const [open, setOpen] = useState(false)

  const recentCount = useMemo(() => {
    if (!entries.length) return 0
    const cutoff = Date.now() - SEVEN_DAYS_MS
    return entries.reduce((acc, e) => {
      const t = new Date(e.at).getTime()
      return Number.isNaN(t) || t < cutoff ? acc : acc + 1
    }, 0)
  }, [entries])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={title}
        aria-label={title}
        className={cn(
          "relative inline-flex h-8 w-8 items-center justify-center rounded-control",
          "border border-border bg-white/5 text-app-muted transition",
          "hover:bg-white/10 hover:text-app-text",
        )}
      >
        <Clock size={16} strokeWidth={2} />
        {recentCount > 0 && (
          <span
            className={cn(
              "absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center",
              "rounded-full border border-app-bg bg-brand-red px-1 text-[10px] font-semibold leading-none text-white",
            )}
          >
            {recentCount > 99 ? "99+" : recentCount}
          </span>
        )}
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} size="xl">
        <DialogHeader
          title={title}
          subtitle={
            entries.length === 0
              ? "No activity recorded yet."
              : `${entries.length} entr${entries.length === 1 ? "y" : "ies"}${recentCount > 0 ? ` · ${recentCount} in last 7 days` : ""}`
          }
        />
        <DialogBody className="max-h-[78vh] overflow-y-auto">
          <ActivityFeedPane entries={entries} />
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}
